/**
 * Next Step AI — the five highest-impact actions for this month, built from
 * the learner's own evidence. One page, two voices: the young person and the
 * parent read the same actions in their own words.
 *
 * Presentation only. Every action, and the decision of how many are visible,
 * comes from the `generate-next-steps` function.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/shell/AppShell";
import PageHead from "@/components/PageHead";
import NextStepCerts from "@/components/certifications/NextStepCerts";
import NextStepRoadmap from "@/components/career/NextStepRoadmap";
import CareerSwitcherRoadmap from "@/components/career/CareerSwitcherRoadmap";
import { useLearnerStart } from "@/hooks/useLearnerStart";
import { isCareerSwitcher } from "@/lib/careerSwitcherRoadmap";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import InfoPopout from "@/components/InfoPopout";
import {
  Sparkles, Loader2, Trophy, GraduationCap, BookOpen, Wrench, Briefcase,
  Sun, Bot, Users, CheckCircle2, Circle, Search, Lock, RefreshCw, Clock,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMembership } from "@/hooks/useMembership";
import { toast } from "sonner";

type Category =
  | "competition" | "course" | "book" | "skill"
  | "internship" | "summer-program" | "ai-tool" | "extracurricular";

interface Action {
  id: string;
  category: Category;
  title: string;
  what: string;
  why: string;
  evidence: string;
  confidence: "exploring" | "emerging" | "strong";
  effort: string;
  steps: string[];
  search_hint: string;
  parent_note: string;
  learner_note: string;
  opens_up: string;
}

interface PlanRow {
  id: string;
  month: string;
  actions: { headline?: string; items?: Action[] } | null;
  progress: Record<string, string> | null;
  locked: boolean;
  total_available: number;
  generated_at: string;
}

const CATEGORY_META: Record<Category, { label: string; icon: typeof Trophy }> = {
  competition: { label: "Competition", icon: Trophy },
  course: { label: "Course", icon: GraduationCap },
  book: { label: "Book", icon: BookOpen },
  skill: { label: "Skill", icon: Wrench },
  internship: { label: "Internship", icon: Briefcase },
  "summer-program": { label: "Summer programme", icon: Sun },
  "ai-tool": { label: "AI tool", icon: Bot },
  extracurricular: { label: "Extracurricular", icon: Users },
};

const CONFIDENCE_LABEL: Record<Action["confidence"], string> = {
  exploring: "Worth exploring",
  emerging: "Emerging signal",
  strong: "Strong signal",
};

function monthKey(d = new Date()) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(key: string) {
  const [y, m] = key.split("-").map(Number);
  if (!y || !m) return key;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

export default function NextSteps() {
  const { user } = useAuth();
  const switcher = isCareerSwitcher(useLearnerStart());
  const { isActive } = useMembership();
  const [voice, setVoice] = useState<"learner" | "parent">("learner");
  const [plan, setPlan] = useState<PlanRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const month = monthKey();

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from("next_step_plans")
      .select("id, month, actions, progress, locked, total_available, generated_at")
      .eq("user_id", user.id)
      .eq("month", month)
      .maybeSingle();
    setPlan((data as unknown as PlanRow) ?? null);
    setLoading(false);
  }, [user?.id, month]);

  useEffect(() => { load(); }, [load]);

  const generate = async (force = false) => {
    setWorking(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-next-steps", {
        body: { month, force },
      });
      // Non-2xx replies arrive as an error with the body tucked inside
      // error.context, so read it before falling back to a raw message.
      if (error) {
        let body: { error?: string; message?: string } | null = null;
        try {
          const res = (error as { context?: Response }).context;
          if (res && typeof res.json === "function") body = await res.clone().json();
        } catch {
          body = null;
        }
        if (body?.message) {
          toast.error(body.message);
          return;
        }
        throw error;
      }
      if (data?.error === "no_evidence") {
        toast.error(data.message);
        return;
      }
      if (data?.error) throw new Error(data.error);
      setPlan(data.plan as PlanRow);
      toast.success("This month's actions are ready");
    } catch (e) {
      toast.error((e as Error).message ?? "We could not build this month's actions");
    } finally {
      setWorking(false);
    }
  };

  const items = useMemo(() => plan?.actions?.items ?? [], [plan]);
  const progress = plan?.progress ?? {};
  const doneCount = items.filter((a) => progress[a.id] === "done").length;

  const toggleDone = async (id: string) => {
    if (!plan) return;
    const next = { ...progress, [id]: progress[id] === "done" ? "open" : "done" };
    setPlan({ ...plan, progress: next });
    const { error } = await supabase
      .from("next_step_plans")
      .update({ progress: next })
      .eq("id", plan.id);
    if (error) toast.error("We could not save that just now");
  };

  const hidden = plan?.locked ? Math.max((plan.total_available || 5) - items.length, 0) : 0;

  return (
    <AppShell title="Next Step">
      <PageHead
        title="Next Step — the 5 things to do this month | WeKIT"
        description="Five specific, doable actions for this month — a competition, a course, a book, a skill — chosen from your own Career Clarity answers."
        path="/next-steps"
      />

      <div className="space-y-6">
        <header className="space-y-3">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
            What to do next, this month
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            Five specific actions for {monthLabel(month)} — drawn from the answers already given,
            not from a generic list. Each one says why it's here and what it opens up.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Tabs value={voice} onValueChange={(v) => setVoice(v as "learner" | "parent")}>
              <TabsList>
                <TabsTrigger value="learner">For me</TabsTrigger>
                <TabsTrigger value="parent">For a parent</TabsTrigger>
              </TabsList>
            </Tabs>
            <InfoPopout label="How these are chosen" title="How these five are chosen">
              <p>
                We look at what's already known about you: your archetype, the careers you
                matched, the skills currently developing, the dreams you pinned and the goals
                you set. Each action names the evidence behind it, so you can judge it yourself.
              </p>
              <p className="mt-2">
                They're possibilities for right now — one way in, never the only way.
              </p>
            </InfoPopout>
          </div>
        </header>

        <CareerSwitcherRoadmap />

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-56 rounded-3xl" />)}
          </div>
        ) : items.length === 0 ? (
          <Card className="border-dashed border-primary/30">
            <CardContent className="p-10 sm:p-12 text-center space-y-4">
              <Sparkles className="h-10 w-10 text-primary mx-auto" />
              <h2 className="font-display text-xl font-semibold text-foreground">
                Build this month's five actions
              </h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                We'll use what you've already told us — your strengths, your matches, the life
                you pinned — and turn it into five things you can actually start this month.
              </p>
              <Button
                onClick={() => generate(false)}
                disabled={working}
                className="rounded-full gap-2 min-h-12"
              >
                {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Show me this month's actions
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {plan?.actions?.headline && (
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-5">
                  <p className="text-foreground/90">{plan.actions.headline}</p>
                </CardContent>
              </Card>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {doneCount} of {items.length} done this month
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => generate(true)}
                disabled={working}
                className="rounded-full gap-2 min-h-11"
              >
                {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Rebuild this month
              </Button>
            </div>

            <section className="grid gap-4 sm:grid-cols-2">
              {items.map((a, idx) => {
                const meta = CATEGORY_META[a.category] ?? CATEGORY_META.skill;
                const Icon = meta.icon;
                const done = progress[a.id] === "done";
                return (
                  <Card
                    key={a.id}
                    className={`rounded-3xl border-border/60 transition-colors ${done ? "bg-muted/40" : ""}`}
                  >
                    <CardContent className="p-5 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <Badge variant="secondary" className="gap-1.5 font-normal">
                          <Icon className="h-3.5 w-3.5" /> {meta.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          Action {idx + 1}
                        </span>
                      </div>

                      <h3 className="font-display text-lg font-semibold leading-tight text-foreground">
                        {a.title}
                      </h3>
                      <p className="text-sm text-foreground/80">{a.what}</p>

                      <div className="rounded-2xl border border-primary/10 bg-primary/5 p-3 space-y-1">
                        <p className="text-sm text-foreground/90">
                          {voice === "parent" ? a.parent_note : a.learner_note}
                        </p>
                        <p className="text-xs text-muted-foreground">{a.why}</p>
                      </div>

                      <ol className="space-y-1.5">
                        {a.steps?.map((s, i) => (
                          <li key={i} className="flex gap-2 text-sm text-foreground/80">
                            <span className="text-primary font-medium shrink-0">{i + 1}.</span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ol>

                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {a.effort}
                        </span>
                        <Badge variant="outline" className="text-[11px] font-normal">
                          {CONFIDENCE_LABEL[a.confidence] ?? "Worth exploring"}
                        </Badge>
                        <span>Based on: {a.evidence}</span>
                      </div>

                      <p className="text-xs text-muted-foreground italic">{a.opens_up}</p>

                      <div className="flex items-center gap-2 pt-3 border-t border-border/50">
                        <Button
                          variant={done ? "secondary" : "default"}
                          size="sm"
                          className="rounded-full gap-1.5 min-h-11 flex-1"
                          onClick={() => toggleDone(a.id)}
                        >
                          {done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                          {done ? "Done" : "Mark done"}
                        </Button>
                        {a.search_hint && (
                          <Button
                            asChild
                            variant="outline"
                            size="sm"
                            className="rounded-full gap-1.5 min-h-11"
                          >
                            <a
                              href={`https://www.google.com/search?q=${encodeURIComponent(a.search_hint)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Search className="h-3.5 w-3.5" /> Find it
                            </a>
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {hidden > 0 && (
                <Card className="rounded-3xl border-dashed border-primary/30 bg-primary/5">
                  <CardContent className="p-6 h-full flex flex-col justify-center text-center space-y-3">
                    <Lock className="h-8 w-8 text-primary mx-auto" />
                    <h3 className="font-display text-lg font-semibold text-foreground">
                      {hidden} more action{hidden > 1 ? "s" : ""} for {monthLabel(month)}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Your first action is always free. Unlock the full set each month —
                      the competition, the course, the book, the skill and the way in.
                    </p>
                    <Button asChild className="rounded-full min-h-12 mt-1">
                      <Link to="/payment">See what's included</Link>
                    </Button>
                  </CardContent>
                </Card>
              )}
            </section>

            {!isActive && hidden === 0 && (
              <p className="text-xs text-muted-foreground">
                Actions refresh each month, so the next step always matches where you are now.
              </p>
            )}
          </>
        )}
        {!switcher && <NextStepRoadmap />}
        <NextStepCerts />
      </div>
    </AppShell>
  );
}
